import { useState } from 'react'
import { ActionChip } from '../../components/cards/actionChip'
import { Checkbox } from '../../components/inputs/Checkbox'
import { TextInput } from '../../components/inputs/textInput'
import {
  INTEREST_STATUS,
  INTEREST_STATUS_LABEL,
} from '../../redux-toolkits/interests/interests.constant'
import { useGetAllInterestsQuery } from '../../redux-toolkits/interests/interests.slice'

export const InterestSelector = ({
  selectedIds,
  onChange,
  description = 'Choose the interests that fit this good list. You can select more than one.',
}: {
  description?: string
  selectedIds: string[]
  onChange?: (ids: string[]) => void
}) => {
  const [search, setSearch] = useState('')
  const {
    data: interests = [],
    isLoading,
    isError,
    refetch,
  } = useGetAllInterestsQuery(null)

  if (isLoading) return <p className="text-sm">Loading interests…</p>
  if (isError) {
    return (
      <div role="alert" className="text-sm">
        Unable to load interests.{' '}
        <button type="button" className="underline" onClick={() => refetch()}>
          Retry
        </button>
      </div>
    )
  }

  if (!onChange) {
    return selectedIds.length ? (
      <div className="flex flex-wrap gap-2">
        {selectedIds.map((id) => {
          const interest = interests.find(({ _id }) => _id === id)
          return (
            <ActionChip
              key={id}
              name={
                interest === undefined
                  ? 'Unavailable interest'
                  : interest.status === INTEREST_STATUS.ACTIVE
                    ? interest.name
                    : `${interest.name} (${
                        INTEREST_STATUS_LABEL[interest.status]
                      })`
              }
            />
          )
        })}
      </div>
    ) : (
      <p className="text-sec-black text-sm">No interests selected</p>
    )
  }

  const selectableInterests = interests.filter(
    (interest) =>
      (interest.status === INTEREST_STATUS.ACTIVE ||
        selectedIds.includes(interest._id)) &&
      interest.name.toLowerCase().includes(search.trim().toLowerCase()),
  )
  const missingIds = selectedIds.filter(
    (id) => !interests.some((interest) => interest._id === id),
  )

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-sec-black">{description}</p>
      <TextInput
        hasIcon={false}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search interests"
      />
      <div className="flex max-h-64 flex-col gap-3 overflow-y-auto">
        {selectableInterests.map((interest) => (
          <Checkbox
            key={interest._id}
            text={
              interest.status === INTEREST_STATUS.ACTIVE
                ? interest.name
                : `${interest.name} (${INTEREST_STATUS_LABEL[interest.status]})`
            }
            checkStatus={selectedIds.includes(interest._id)}
            handleChange={(checked) =>
              onChange(
                checked
                  ? [...selectedIds, interest._id]
                  : selectedIds.filter((id) => id !== interest._id),
              )
            }
          />
        ))}
        {missingIds.map((id) => (
          <Checkbox
            key={id}
            text="Unavailable interest"
            checkStatus
            handleChange={() =>
              onChange(selectedIds.filter((selectedId) => selectedId !== id))
            }
          />
        ))}
      </div>
      {!selectableInterests.length && !missingIds.length && (
        <p className="text-sec-black text-sm">
          {search ? 'No matching interests' : 'No active interests available'}
        </p>
      )}
    </div>
  )
}
